import { Order, OrderFormData, OrderWithProduct, OrderWithDetails, OrderFromQuoteData } from '../types/order';
import { Product } from '../types/product';
import { QuoteWithDetails } from './quoteService';
import { supabase } from '../lib/supabase';
import { uploadImage } from './cloudinaryService';
import { saleService } from './saleService';
import { Sale } from '../types/sale';

// Exportar para que sea considerado un módulo
export {};

// Convertir datos de Supabase a formato Order
const convertSupabaseOrder = (supabaseOrder: any): Order => ({
  id: supabaseOrder.id,
  order_number: supabaseOrder.order_number,
  customer_name: supabaseOrder.customer_name,
  customer_id: supabaseOrder.customer_id,
  quote_id: supabaseOrder.quote_id,
  product_id: supabaseOrder.product_id,
  quantity: supabaseOrder.quantity,
  unit_price: supabaseOrder.unit_price,
  subtotal: supabaseOrder.subtotal,
  igv_percentage: supabaseOrder.igv_percentage,
  igv_amount: supabaseOrder.igv_amount,
  total_with_igv: supabaseOrder.total_with_igv,
  currency: supabaseOrder.currency,
  payment_terms: supabaseOrder.payment_terms,
  payment_type: supabaseOrder.payment_type || 'contado',
  credit_days: supabaseOrder.credit_days || 0,
  delivery_days: supabaseOrder.delivery_days,
  delivery_time: supabaseOrder.delivery_time,
  notes: supabaseOrder.notes,
  calculate_detraccion: supabaseOrder.calculate_detraccion,
  detraccion_percentage: supabaseOrder.detraccion_percentage,
  detraccion_amount: supabaseOrder.detraccion_amount,
  order_document: supabaseOrder.order_document,
  order_document_url: supabaseOrder.order_document_url,
  invoice_document: supabaseOrder.invoice_document,
  invoice_document_url: supabaseOrder.invoice_document_url,
  status: supabaseOrder.status,
  order_date: supabaseOrder.order_date,
  created_at: supabaseOrder.created_at,
  updated_at: supabaseOrder.updated_at
});

// Convertir datos de Supabase a formato OrderWithProduct
const convertSupabaseOrderWithProduct = (supabaseOrder: any, product: Product): OrderWithProduct => ({
  ...convertSupabaseOrder(supabaseOrder),
  product
});

// Convertir datos de Supabase a formato OrderWithDetails
const convertSupabaseOrderWithDetails = (
  supabaseOrder: any, 
  product: Product, 
  quote?: QuoteWithDetails
): OrderWithDetails => ({
  ...convertSupabaseOrder(supabaseOrder),
  product,
  quote
});

// Generar número único de orden de pedido
const generateOrderNumber = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `OP-${year}${month}${day}-${random}`;
};

// Servicio de órdenes de pedido con Supabase
export const orderService = {
  // Obtener todas las órdenes
  getOrders: async (): Promise<OrderWithDetails[]> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products:product_id (*),
          quotes:quote_id (*)
        `)
        .order('order_date', { ascending: false });

      if (error) {
        console.error('Error al obtener órdenes:', error);
        return [];
      }

      return data.map((item: any) => {
        const product = item.products;
        const quote = item.quotes;
        return convertSupabaseOrderWithDetails(item, product, quote);
      });
    } catch (error) {
      console.error('Error inesperado al obtener órdenes:', error);
      return [];
    }
  },

  // Obtener una orden por ID
  getOrderById: async (id: number): Promise<OrderWithDetails | null> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products:product_id (*),
          quotes:quote_id (*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener orden por ID:', error);
        return null;
      }

      const product = data.products;
      const quote = data.quotes;
      return convertSupabaseOrderWithDetails(data, product, quote);
    } catch (error) {
      console.error('Error inesperado al obtener orden por ID:', error);
      return null;
    }
  },

  // Obtener datos de una cotización para crear una orden
  getOrderDataFromQuote: async (quoteId: number): Promise<OrderFromQuoteData | null> => {
    try {
      const { data: quote, error } = await supabase
        .from('quotes')
        .select(`
          *,
          quote_details (
            product_id,
            quantity,
            unit_price,
            products (
              id,
              name
            )
          )
        `)
        .eq('id', quoteId)
        .single();

      if (error) {
        console.error('Error al obtener cotización:', error);
        return null;
      }

      if (!quote.quote_details || quote.quote_details.length === 0) {
        console.error('La cotización no tiene detalles de productos');
        return null;
      }

      // Tomar el primer producto de la cotización (asumimos que hay uno solo por simplicidad)
      const firstDetail = quote.quote_details[0];
      
      return {
        quote_id: quote.id,
        customer_name: quote.company || quote.name,
        customer_id: undefined, // No tenemos customer_id en la cotización actual
        product_id: firstDetail.product_id,
        quantity: firstDetail.quantity,
        unit_price: firstDetail.unit_price,
        currency: 'PEN', // Por defecto
        payment_terms: quote.payment_terms || '',
        delivery_days: quote.delivery_days || 5,
        delivery_time: quote.delivery_terms || ''
      };
    } catch (error) {
      console.error('Error inesperado al obtener datos de cotización:', error);
      return null;
    }
  },

  // Agregar una nueva orden
  addOrder: async (orderData: OrderFormData): Promise<Order | null> => {
    try {
      // Calcular montos basados en los items
      const subtotal = orderData.items.reduce((sum, item) => sum + item.subtotal, 0);
      const igvAmount = subtotal * (orderData.calculate_detraccion ? 0.18 : 0); // 18% de IGV
      const totalWithIgv = subtotal + igvAmount;
      const detraccionAmount = orderData.calculate_detraccion ? totalWithIgv * (orderData.detraccion_percentage / 100) : 0;

      // Subir documento de orden si existe y es un archivo File
      let orderDocumentUrl = '';
      if (orderData.order_document && orderData.order_document instanceof File) {
        try {
          const uploadResult = await uploadImage(orderData.order_document, {
            folder: 'atomic-corporation/orders',
            resource_type: 'auto'
          });

          orderDocumentUrl = uploadResult.secure_url;
          console.log('✅ Documento de orden subido exitosamente a Cloudinary:', uploadResult.secure_url);
        } catch (uploadError) {
          console.error('Error al subir documento de orden:', uploadError);
          // Continuar sin el documento si hay error en la subida
        }
      }

      // Tomar el primer producto como producto principal (para compatibilidad con la estructura actual)
      const firstItem = orderData.items[0];

      // Preparar datos de inserción solo con campos que existen en la tabla
      const insertData: any = {
        order_number: orderData.order_number || generateOrderNumber(),
        customer_name: orderData.customer_name,
        customer_id: orderData.customer_id,
        quote_id: orderData.quote_id,
        product_id: firstItem?.product_id || 0,
        quantity: firstItem?.quantity || 0,
        unit_price: firstItem?.unit_price || 0,
        subtotal: subtotal,
        igv_percentage: orderData.calculate_detraccion ? 18 : 0,
        igv_amount: igvAmount,
        total_with_igv: totalWithIgv,
        currency: orderData.currency || 'USD',
        payment_terms: orderData.payment_terms,
        delivery_days: orderData.delivery_days || 5,
        delivery_time: orderData.delivery_time,
        notes: orderData.notes,
        calculate_detraccion: orderData.calculate_detraccion || false,
        detraccion_percentage: orderData.detraccion_percentage || 12,
        detraccion_amount: detraccionAmount,
        order_document: orderData.order_document?.name,
        order_document_url: orderDocumentUrl,
        status: 'generated',
        order_date: orderData.order_date || new Date().toLocaleDateString('en-CA')
      };

      // Agregar campos de pago (ya existen en la tabla)
      insertData.payment_type = orderData.payment_type || 'contado';
      insertData.credit_days = orderData.credit_days || 0;

      console.log('📝 Datos a insertar en orders:', JSON.stringify(insertData, null, 2));

      const { data, error } = await supabase
        .from('orders')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Error al agregar orden:', error);
        return null;
      }

      // Actualizar el stock de todos los productos (restar la cantidad vendida)
      for (const item of orderData.items) {
        await updateProductStock(item.product_id, -item.quantity);
      }

      // NOTA: Las ventas solo se generan automáticamente cuando la orden cambia a estado "Entregada"
      // No generar venta al crear la orden
      console.log('📝 Orden creada sin generar venta (se generará al marcar como entregada):', data.order_number);

      return convertSupabaseOrder(data);
    } catch (error) {
      console.error('Error inesperado al agregar orden:', error);
      return null;
    }
  },

  // Actualizar una orden existente
  updateOrder: async (id: number, updatedOrder: Partial<OrderFormData>): Promise<Order | null> => {
    try {
      const updateData: any = {};
      
      if (updatedOrder.customer_name !== undefined) updateData.customer_name = updatedOrder.customer_name;
      if (updatedOrder.customer_id !== undefined) updateData.customer_id = updatedOrder.customer_id;
      if (updatedOrder.quote_id !== undefined) updateData.quote_id = updatedOrder.quote_id;
      if (updatedOrder.currency !== undefined) updateData.currency = updatedOrder.currency;
      if (updatedOrder.payment_terms !== undefined) updateData.payment_terms = updatedOrder.payment_terms;
      if (updatedOrder.delivery_days !== undefined) updateData.delivery_days = updatedOrder.delivery_days;
      if (updatedOrder.delivery_time !== undefined) updateData.delivery_time = updatedOrder.delivery_time;
      if (updatedOrder.notes !== undefined) updateData.notes = updatedOrder.notes;
      if (updatedOrder.calculate_detraccion !== undefined) updateData.calculate_detraccion = updatedOrder.calculate_detraccion;
      if (updatedOrder.detraccion_percentage !== undefined) updateData.detraccion_percentage = updatedOrder.detraccion_percentage;
      if (updatedOrder.order_date !== undefined) updateData.order_date = updatedOrder.order_date;
      if (updatedOrder.order_number !== undefined) updateData.order_number = updatedOrder.order_number;

      // Recalcular montos si se modificaron los valores relevantes
      if (updatedOrder.items !== undefined ||
          updatedOrder.calculate_detraccion !== undefined ||
          updatedOrder.detraccion_percentage !== undefined) {
        
        // Obtener la orden actual para los valores que no se actualizan
        const currentOrder = await orderService.getOrderById(id);
        if (currentOrder) {
          const items = updatedOrder.items ?? [{
            product_id: currentOrder.product_id,
            product_name: currentOrder.product?.name || '',
            quantity: currentOrder.quantity,
            unit_price: currentOrder.unit_price,
            subtotal: currentOrder.subtotal
          }];
          
          const calculateDetraccion = updatedOrder.calculate_detraccion ?? currentOrder.calculate_detraccion;
          const detraccionPercentage = updatedOrder.detraccion_percentage ?? currentOrder.detraccion_percentage;
          
          const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
          const igvAmount = calculateDetraccion ? subtotal * 0.18 : 0;
          const totalWithIgv = subtotal + igvAmount;
          const detraccionAmount = calculateDetraccion ? totalWithIgv * (detraccionPercentage / 100) : 0;
          
          updateData.subtotal = subtotal;
          updateData.igv_percentage = calculateDetraccion ? 18 : 0;
          updateData.igv_amount = igvAmount;
          updateData.total_with_igv = totalWithIgv;
          updateData.detraccion_amount = detraccionAmount;
          
          // Actualizar el primer producto para compatibilidad con la estructura actual
          if (items.length > 0) {
            const firstItem = items[0];
            updateData.product_id = firstItem.product_id;
            updateData.quantity = firstItem.quantity;
            updateData.unit_price = firstItem.unit_price;
          }
        }
      }

      // Subir nuevo documento de orden solo si se proporciona un nuevo archivo File
      if (updatedOrder.order_document && updatedOrder.order_document instanceof File) {
        try {
          console.log('🔄 Subiendo nuevo documento para orden actualizada...');
          const uploadResult = await uploadImage(updatedOrder.order_document, {
            folder: 'atomic-corporation/orders',
            resource_type: 'auto'
          });
          updateData.order_document = updatedOrder.order_document.name;
          updateData.order_document_url = uploadResult.secure_url;
          console.log('✅ Nuevo documento de orden subido exitosamente:', uploadResult.secure_url);
        } catch (uploadError) {
          console.error('Error al subir documento de orden:', uploadError);
        }
      }

      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar orden:', error);
        return null;
      }

      return convertSupabaseOrder(data);
    } catch (error) {
      console.error('Error inesperado al actualizar orden:', error);
      return null;
    }
  },

  // Actualizar estado de una orden
  updateOrderStatus: async (id: number, status: Order['status']): Promise<Order | null> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar estado de orden:', error);
        return null;
      }

      const updatedOrder = convertSupabaseOrder(data);

      // Si el estado es "delivered", generar automáticamente una venta
      if (status === 'delivered' || status === 'Entregada') {
        try {
          console.log('🔄 Generando venta automáticamente al marcar orden como entregada:', updatedOrder.order_number);
          console.log('📊 Datos de la orden actualizada:', JSON.stringify(updatedOrder, null, 2));
          
          const saleData = {
            order_id: updatedOrder.id, // Referencia a la orden de pedido
            order_number: updatedOrder.order_number, // Número de la orden de pedido
            order_date: updatedOrder.order_date, // Fecha de generación de la orden
            // No incluir delivery_date ni delivery_time en ventas generadas desde órdenes
            customer_id: updatedOrder.customer_id,
            customer_name: updatedOrder.customer_name,
            product_id: updatedOrder.product_id,
            quantity: updatedOrder.quantity,
            unit_price: updatedOrder.unit_price,
            subtotal: updatedOrder.subtotal,
            igv_percentage: updatedOrder.igv_percentage,
            igv_amount: updatedOrder.igv_amount,
            total_with_igv: updatedOrder.total_with_igv,
            sale_date: new Date().toLocaleDateString('en-CA'), // Fecha de generación de la venta
            payment_method: 'transferencia',
            payment_terms: updatedOrder.payment_terms || 'Contado',
            credit_days: updatedOrder.credit_days || 0, // Agregar días de crédito desde la orden
            payment_type: updatedOrder.payment_type || 'contado', // Agregar tipo de pago desde la orden
            notes: `Venta generada automáticamente desde la orden de pedido ${updatedOrder.order_number} al ser entregada`
          };

          console.log('📝 Datos para crear venta:', JSON.stringify(saleData, null, 2));
          console.log('🔄 Llamando a saleService.createSale...');
          
          const newSale = await saleService.createSale(saleData);
          
          console.log('📊 Respuesta de saleService.createSale:', newSale);
          
          if (newSale) {
            console.log('✅ Venta generada automáticamente con referencia a orden:', newSale.sale_number);
            console.log('📋 Detalles de la venta creada:', JSON.stringify(newSale, null, 2));
          } else {
            console.error('❌ Error al generar venta automáticamente desde orden entregada - createSale retornó null');
          }
        } catch (saleError) {
          console.error('❌ Error al generar venta automáticamente:', saleError);
          if (saleError instanceof Error) {
            console.error('Stack trace del error:', saleError.stack);
          }
          console.error('Tipo de error:', typeof saleError);
          console.error('Error completo:', JSON.stringify(saleError, null, 2));
        }
      }

      // Actualizar estado de la venta asociada (si existe)
      await updateAssociatedSaleStatus(id, status);

      return updatedOrder;
    } catch (error) {
      console.error('Error inesperado al actualizar estado de orden:', error);
      return null;
    }
  },

  // Facturar una orden (subir factura PDF)
  invoiceOrder: async (id: number, invoiceFile: File): Promise<{ success: boolean; invoiceUrl?: string; error?: string }> => {
    try {
      console.log('🔄 Facturando orden:', id);
      
      // Subir la factura a Cloudinary
      let invoiceUrl = '';
      try {
        const uploadResult = await uploadImage(invoiceFile, {
          folder: 'atomic-corporation/invoices',
          resource_type: 'auto'
        });

        invoiceUrl = uploadResult.secure_url;
        console.log('✅ Factura subida exitosamente a Cloudinary:', uploadResult.secure_url);
      } catch (uploadError) {
        console.error('Error al subir factura:', uploadError);
        return {
          success: false,
          error: 'Error al subir la factura a Cloudinary: ' + (uploadError instanceof Error ? uploadError.message : String(uploadError))
        };
      }

      // Actualizar la orden con la factura
      const { data, error } = await supabase
        .from('orders')
        .update({
          status: 'invoiced',
          invoice_document: invoiceFile.name,
          invoice_document_url: invoiceUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar orden con factura:', error);
        return {
          success: false,
          error: 'Error al actualizar la orden con la factura: ' + (error instanceof Error ? error.message : String(error))
        };
      }

      console.log('✅ Orden facturada exitosamente:', data.order_number);
      
      // Actualizar estado de la venta asociada
      await updateAssociatedSaleStatus(id, 'invoiced');
      
      return {
        success: true,
        invoiceUrl: invoiceUrl
      };
    } catch (error) {
      console.error('Error inesperado al facturar orden:', error);
      return {
        success: false,
        error: 'Error inesperado al facturar la orden: ' + (error instanceof Error ? error.message : String(error))
      };
    }
  },

  // Eliminar una orden
  deleteOrder: async (id: number): Promise<boolean> => {
    try {
      // Obtener la orden antes de eliminarla para actualizar el stock
      const order = await orderService.getOrderById(id);
      if (!order) {
        return false;
      }

      // Verificar si hay ventas asociadas a esta orden
      const { data: associatedSales, error: salesError } = await supabase
        .from('sales')
        .select('id, sale_number, status')
        .eq('order_id', id);

      if (salesError) {
        console.error('Error al verificar ventas asociadas:', salesError);
        return false;
      }

      // Si hay ventas asociadas, no permitir eliminar la orden
      if (associatedSales && associatedSales.length > 0) {
        console.error('❌ No se puede eliminar la orden porque tiene ventas asociadas:', {
          orderNumber: order.order_number,
          orderId: id,
          associatedSales: associatedSales.map(s => `${s.sale_number} (${s.status})`)
        });
        return false;
      }

      console.log('🔄 Eliminando orden sin ventas asociadas:', order.order_number);

      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar orden:', error);
        return false;
      }

      // Devolver el stock al producto (sumar la cantidad que se había restado)
      await updateProductStock(order.product.id, order.quantity);

      console.log('✅ Orden eliminada exitosamente:', order.order_number);
      return true;
    } catch (error) {
      console.error('Error inesperado al eliminar orden:', error);
      return false;
    }
  },

  // Obtener órdenes por estado
  getOrdersByStatus: async (status: Order['status']): Promise<OrderWithDetails[]> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products:product_id (*),
          quotes:quote_id (*)
        `)
        .eq('status', status)
        .order('order_date', { ascending: false });

      if (error) {
        console.error('Error al obtener órdenes por estado:', error);
        return [];
      }

      return data.map((item: any) => {
        const product = item.products;
        const quote = item.quotes;
        return convertSupabaseOrderWithDetails(item, product, quote);
      });
    } catch (error) {
      console.error('Error inesperado al obtener órdenes por estado:', error);
      return [];
    }
  },

  // Obtener estadísticas de órdenes
  getOrderStats: async (): Promise<{
    total: number;
    generated: number;
    delivered: number;
    invoiced: number;
    paid: number;
  }> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('status');

      if (error) {
        console.error('Error al obtener estadísticas de órdenes:', error);
        return {
          total: 0,
          generated: 0,
          delivered: 0,
          invoiced: 0,
          paid: 0
        };
      }

      const stats = {
        total: data?.length || 0,
        generated: data?.filter(o => o.status === 'generated').length || 0,
        delivered: data?.filter(o => o.status === 'delivered').length || 0,
        invoiced: data?.filter(o => o.status === 'invoiced').length || 0,
        paid: data?.filter(o => o.status === 'paid').length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error inesperado al obtener estadísticas de órdenes:', error);
      return {
        total: 0,
        generated: 0,
        delivered: 0,
        invoiced: 0,
        paid: 0
      };
    }
  }
};

// Función para actualizar automáticamente el estado de la venta asociada a una orden
const updateAssociatedSaleStatus = async (orderId: number, orderStatus: OrderWithDetails['status']): Promise<void> => {
  try {
    // Buscar la venta asociada a esta orden
    const { data: sales, error: salesError } = await supabase
      .from('sales')
      .select('*')
      .eq('order_id', orderId);

    if (salesError) {
      console.error('Error al buscar venta asociada:', salesError);
      return;
    }

    if (!sales || sales.length === 0) {
      console.log(`📝 No hay venta asociada a la orden ${orderId}`);
      return;
    }

    const sale = sales[0];
    let newSaleStatus: Sale['status'];
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    // Mapear el estado de la orden al estado de la venta
    switch (orderStatus) {
      case 'generated':
        newSaleStatus = 'Generada';
        break;
      case 'delivered':
        newSaleStatus = 'Generada'; // La venta sigue generada hasta que se facture
        break;
      case 'invoiced':
        newSaleStatus = 'Facturada';
        
        // Si la venta es de crédito, calcular fecha de vencimiento
        if (sale.payment_type === 'credito' && sale.credit_days > 0) {
          const today = new Date();
          const dueDate = new Date(today);
          dueDate.setDate(today.getDate() + sale.credit_days);
          
          updateData.credit_due_date = dueDate.toISOString().split('T')[0];
          console.log(`📅 Fecha de vencimiento calculada para venta ${sale.sale_number}: ${updateData.credit_due_date} (${sale.credit_days} días de crédito)`);
        }
        break;
      case 'paid':
        newSaleStatus = 'Completada';
        break;
      default:
        console.log(`⚠️ Estado de orden no reconocido: ${orderStatus}`);
        return;
    }

    updateData.status = newSaleStatus;

    // Actualizar el estado de la venta
    const { error: updateError } = await supabase
      .from('sales')
      .update(updateData)
      .eq('id', sale.id);

    if (updateError) {
      console.error('Error al actualizar estado de venta asociada:', updateError);
    } else {
      console.log(`✅ Venta ${sale.sale_number} actualizada a ${newSaleStatus} automáticamente por cambio de orden ${orderId} a ${orderStatus}`);
      if (updateData.credit_due_date) {
        console.log(`📅 Fecha de vencimiento configurada: ${updateData.credit_due_date}`);
      }
    }
  } catch (error) {
    console.error('Error inesperado al actualizar venta asociada:', error);
  }
};

// Función auxiliar para actualizar el stock de un producto
const updateProductStock = async (productId: number, quantityToAdd: number): Promise<void> => {
  try {
    // Obtener el stock actual del producto
    const { data: product, error: productError } = await supabase
      .from('products')
      .select('stock')
      .eq('id', productId)
      .single();

    if (productError) {
      console.error('Error al obtener stock del producto:', productError);
      return;
    }

    // Calcular nuevo stock
    const currentStock = product.stock || 0;
    const newStock = Math.max(0, currentStock + quantityToAdd);

    // Actualizar stock y estado inStock
    const { error: updateError } = await supabase
      .from('products')
      .update({
        stock: newStock,
        in_stock: newStock > 0
      })
      .eq('id', productId);

    if (updateError) {
      console.error('Error al actualizar stock del producto:', updateError);
    }
  } catch (error) {
    console.error('Error inesperado al actualizar stock:', error);
  }
};